import React, { useState, useEffect, useRef } from "react";
import _ from "lodash";
import {
  _getSelectedOption,
  _getBaseUrl,
  _isFirstRender,
  _getCSRFTokenValue,
} from "../utils/helper";
import api from "../utils/api";
import { Loader, Pagination } from "../atoms";
import { FilterDropDown, KeywordSearch } from "../forms";
import { ActivityCard } from "../cards";
import StateView from "../utils/StateView";

let INITIAL_VALUES = {
  gebieden: [],
  onderwerpen: [],
  skip: 0,
  take: 10,
  nodeType: "",
  group: "",
  keywords: [],
  activityType: "",
  blockType: ""
};

const Activiteiten = (props) => {
  const [filterOptions, setFilterOptions] = useState([]);
  const [values, setValues] = useState(INITIAL_VALUES);
  const [posts, setPosts] = useState([]);
  const [totalPosts, setTotalPosts] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);
  const [isLoadingPosts, setIsLoadingPosts] = useState(false);
  const [isFilter, setIsFilter] = useState(false);
  const [tags, setTags] = useState([]);
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [visiblePosts, setVisiblePosts] = useState(4);

  const isFirstLoad = _isFirstRender();
  const nodeType = props?.data["node-type"] || "";
  const activityType = props?.data["activity-type"] || "";
  const blockType = props?.data["block-type"] || "";
  const group = props?.data["group"] || "";
  const observerRef = useRef(null);

  useEffect(() => {
    fetchFilters();

    const urlParams = new URLSearchParams(window.location.search);
    const keyword = decodeURIComponent(urlParams.get("keyword"));
    const topic = decodeURIComponent(urlParams.get("topic"));

    if (keyword !== null && keyword !== "" && keyword !== "null") {
      setValues({ ...INITIAL_VALUES, ["keywords"]: [keyword], ["skip"]: 0 });
      addRemoveTags("keywords", keyword, true);
      return;
    }

    if (topic !== null && topic !== "" && topic !== "null") {
      setValues({ ...INITIAL_VALUES, ["topics"]: [topic], ["skip"]: 0 });
      return;
    }

    // setValues({ ...INITIAL_VALUES, ["nodeType"]: nodeType, ["group"]: group });

    // if (nodeType === "actueel") {
    setValues({ ...INITIAL_VALUES, ["take"]: 12, ["nodeType"]: nodeType, ["activityType"]: activityType });
    // }

    // fetchPosts();
  }, []);

  useEffect(() => {
    if (!isFirstLoad) {
      fetchPosts();
    }
  }, [values]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && posts.length < totalPosts && !isLoadingPosts) {
          setValues((prevValues) => ({ ...prevValues, skip: prevValues.skip + prevValues.take }));
        }
      },
      { threshold: 1.0 }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observer.unobserve(observerRef.current);
      }
    };
  }, [posts, totalPosts, isLoadingPosts]);

  const fetchFilters = () => {
    setIsLoadingFilters(true);

    api
      .get("/filterlistoptions")
      .then((res) => {
        setIsLoadingFilters(false);
        setFilterOptions(res.data);

        const urlParams = new URLSearchParams(window.location.search);
        const topic = decodeURIComponent(urlParams.get("topic"));

        if (topic !== null && topic !== "" && topic !== "null") {
          addRemoveTags("topics", topic, true);
        }
      })
      .catch(() => {
        setIsLoadingFilters(false);
      });
  };

  const fetchPosts = async () => {
    setIsLoadingPosts(true);

    const csrfToken = await _getCSRFTokenValue();
    const fields = values;
    api
      .post("/searchandfilter", fields, {
        headers: {
          "X-CSRF-Token": csrfToken,
        },
      })
      .then((res) => {
        setIsLoadingPosts(false);
        setPosts((prevPosts) =>
          values.skip === 0 ? res.data.posts : [...prevPosts, ...res.data.posts]
        );
        setTotalPosts(res.data.totalNodes);
      })
      .catch(() => {
        setIsLoadingPosts(false);
      });
  };

  const loadMore = () => {
    setVisiblePosts((prev) => prev + 4); // Load 4 more posts each time
  };

  const updateFilters = (key, value) => {
    let filters = { ...values, [key]: value, skip: 0 };

    setCurrentPage(1);
    setPosts([]); // Clear existing posts when filters change
    setValues(filters);
  };

  const addRemoveTags = (filter, value, addRemove) => {
    let temp = [...tags];

    if (addRemove) {
      temp.push({
        filter,
        value,
      });
    } else {
      temp = temp.filter((item) => !(item.filter === filter && item.value === value));
    }

    setTags(temp);
  };

  const onFilterChanged = (filter, value, checked) => {
    let updatedValue = [...values[filter]];

    if (checked) {
      addRemoveTags(filter, value, true);
      updatedValue.push(value);
    } else {
      addRemoveTags(filter, value, false);
      updatedValue = updatedValue.filter((item) => item !== value);
    }

    updateFilters(filter, updatedValue);
    setIsFilter(true);
  };

  const onKeyUpHandler = (e) => {
    if (e.key === "Enter") {
      const keyword = e.target.value;
      setSearchValue(keyword);
      const isWhitespaceString = !keyword.replace(/\s/g, "").length;

      if (isWhitespaceString) {
        return;
      }

      let updatedKeywords = [...values.keywords];

      if (updatedKeywords.indexOf(keyword) >= 0) {
        return;
      }

      updatedKeywords.push(keyword);

      updateFilters("keywords", updatedKeywords);
      addRemoveTags("keywords", keyword, true);
      setIsFilter(true);

      e.target.value = "";
    }
  };

  const removeKeyword = (keyword) => {
    let updatedKeywords = [...values.keywords];

    updatedKeywords = updatedKeywords.filter((item) => item !== keyword);

    updateFilters("keywords", updatedKeywords);
    addRemoveTags("keywords", keyword, false);

    if (updatedKeywords.length === 0) {
      setSearchValue("");
    }
  };

  const clearFilters = () => {
    setSearchValue("");
    setVisiblePosts(4);
    setValues({
      ...INITIAL_VALUES,
      nodeType: values.nodeType,
      activityType: values.activityType,
      group: values.group,
    });
    setTags([]);
    setIsFilter(false);
  };

  const navigatePage = (goNext) => {
    let page;
    let skip;

    if (goNext) {
      page = currentPage + 1;
    } else {
      page = currentPage - 1;
    }

    skip = (page - 1) * values.take;

    setValues({ ...values, skip });
    setCurrentPage(page);
  };

  let splashbottom = _getBaseUrl("themes/custom/tema/src/assets/sb.webp");
  let splashtop = _getBaseUrl("themes/custom/tema/src/assets/st.webp");

  return (
    <React.Fragment>

      {
        activityType != "past" && blockType != "content" && (
          <div className="container px-0">
            <div className="w-full xl:w-3/5 md:grid md:grid-cols-3 gap-4">
              <KeywordSearch
                placeholder="Zoek"
                onKeyUp={onKeyUpHandler}
                searchValue={searchValue}
                setSearchValue={setSearchValue}
              />

              <div className="w-full pt-4 md:pt-0 flex flex-col md:flex-row justify-between md:justify-start gap-4 md:col-span-2">
                <FilterDropDown
                  placeholder="onderwerpen"
                  filter="onderwerpen"
                  options={filterOptions.onderwerpen}
                  onChange={onFilterChanged}
                  value={values.onderwerpen}
                  nodeType={nodeType}
                  trayClass="w-full mt-2"
                />

                <FilterDropDown
                  placeholder="gebieden"
                  filter="gebieden"
                  options={filterOptions.gebieden}
                  onChange={onFilterChanged}
                  value={values.gebieden}
                  nodeType={nodeType}
                  trayClass="w-full mt-2"
                />

              </div>
            </div>

            {tags.length > 0 && isFilter && (
              <div className="py-4">
                <h2 className="text-lg mb-3 font-semibold">
                  Toegepaste filters
                </h2>
                <ul className="filter-tags--wrapper flex flex-wrap gap-6">
                  {tags.map((item) => {

                    if (item.filter === "keywords") {

                      return (
                        <li key={item.value}>
                          <button
                            className="filter-tag"
                            key={item.value}
                            onClick={() => removeKeyword(item.value)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") removeKeyword(item.value);
                            }}
                            aria-label={`Verwijder trefwoord ${item.value}`}>
                            <span>{item.value}</span>
                            <div
                              className="filter-tag--remove"
                            ></div>
                          </button>
                        </li>
                      );
                    } else {
                      return (
                        <li key={item.value}>
                          <button className="filter-tag"
                            key={`${item.filter}-${item.value}`}
                            onClick={() => onFilterChanged(item.filter, item.value, false)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                onFilterChanged(item.filter, item.value, false);
                              }
                            }}
                            aria-label={`Verwijder filter ${_getSelectedOption(
                              filterOptions[item.filter],
                              item.value,
                              true
                            )?.label || ""}`}
                          >
                            <span>
                              {_getSelectedOption(
                                filterOptions[item.filter],
                                item.value,
                                true
                              )?.label}
                            </span>
                            <div className="filter-tag--remove"></div>
                          </button>
                        </li>
                      );
                    }
                  })}
                  <li>
                    <button className="filter-tag clear cursor-pointer"
                      onClick={() => clearFilters()}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") clearFilters();
                      }}
                      aria-label={`Verwijder alle filters`}
                    >
                      <span>Alles wissen</span>
                    </button>
                  </li>
                </ul>
              </div>
            )
            }
          </div>
        )
      }

      {
        activityType !== "past" && blockType !== "content" && (
          <div className="w-full">
            <img src={splashtop} alt="" className="w-full h-full" />
          </div>
        )
      }

      <div className={`${activityType !== "past" && blockType !== "content" ? "bg-gewLightGrey py-8" : ""}`}>
        <div className="container">
          <div className="my-8">
            {isLoadingPosts && <Loader className="mb-8" />}

            {nodeType === "activities" && blockType !== "content" && (
              <h2 className="h1 mb-8 md:mb-4">
                {activityType === "past" ? "Afgelopen" : "Aankomend"}
              </h2>
            )}

            {/* <div className="text-center text-xl md:text-2xl lg:text-3xl mb-8 md:mb-4" role="status" aria-live="polite">
              {totalPosts} resultaten
            </div> */}

            {_.isArray(posts) && posts.length > 0 ? (
              <React.Fragment>
                <ul className="grid grid-cols-1 items-center gap-8 py-8 list-none ps-0">
                  {posts.slice(0, visiblePosts).map((post, index) => (
                    <ActivityCard key={index} data={post} index={index} nodeType={nodeType} />
                  ))}
                </ul>
                {visiblePosts < posts.length && (
                  <div className="flex justify-center mt-4">
                    <div className="btn w-full md:w-fit">
                      <button
                        className="button-gew inline-block text-center btn-large text-black text-[20px] uppercase overview-btn"
                        onClick={loadMore}>
                        Meer laden
                      </button>
                    </div>
                  </div>
                )}
              </React.Fragment>
            ) : isLoadingPosts ? null : (
              <p className="md:w-4/5">Op dit moment zijn er geen aankomende activiteiten. Blijf op de hoogte door je aan te melden voor onze nieuwsbrief en neem alvast een kijkje bij de afgelopen activiteiten!</p>
            )}
          </div>
        </div>
      </div>


      {
        activityType !== "past" && blockType !== "content" && (
          <div className="w-full">
            <img src={splashbottom} alt="" className="w-full h-full" />
          </div>
        )
      }

      {/* {<StateView title="values" values={values} floating />} */}
      {/* {<StateView title="tags" values={tags} class="bottom-4 left-4 right-[unset]" floating />} */}
      {/* {<StateView title="posts" values={posts} class="top-4 right-4" floating collapsed/>} */}
      {/* {<StateView title="total posts" values={totalPosts} class="top-4 bottom-[unset] left-4 right-[unset]" floating />} */}
      {/* <StateView title="filters" values={filterOptions} class="bottom-4 right-96 max-h-[90vh] overflow-y-auto" floating /> */}
    </React.Fragment>
  );
};

export default Activiteiten;
